import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts } from '../api';
import { NumberSearch, CartPanel } from '../NumberPicker';
import { PlanPicker } from './TSSetup';
import { Page, Section, Stat, Field, Table, Td, Pill, Empty, Loading, ErrorNote, useLoad, useTS, money, when } from '../ui';

const INVOICE_LABELS = {
  renewal: (i) => `Renewal · ${i.seats} seat(s) on ${i.plan}`,
  seat_addition: (i) => `Added ${i.seats_added} seat(s), prorated`,
  plan_upgrade: (i) => `Upgrade ${i.from_plan} → ${i.to_plan}, prorated`,
  final_usage: () => 'Final extra usage',
};

export default function TSBilling() {
  const session = useTS();
  const { data, loading, error, reload } = useLoad(() => ts.billing());
  const numbers = useLoad(() => ts.numbers());
  const [cart, setCart] = useState(null);
  const [extra, setExtra] = useState(1);
  const [dropping, setDropping] = useState([]);
  const [limit, setLimit] = useState('');
  const adding = !!session.company.adding_seats;

  useEffect(() => { if (adding) ts.cart().then(setCart).catch(() => {}); }, [adding]);
  useEffect(() => { if (data) setLimit(String((data.spending_limit_cents || 0) / 100)); }, [data]);

  const run = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await session.refresh(); reload(); numbers.reload(); return r; } catch (e) { toast.error(e.message); return null; }
  };

  if (loading && !data) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const p = data.pricing;
  const u = data.usage;
  const planName = (id) => (p.plans.find(x => x.id === id) || {}).name || id;
  const cycleAlerts = (data.alerts || []).filter(a => a.cycle === u.cycle);
  const changePlan = async (id) => {
    const up = p.plans.findIndex(x => x.id === id) > p.plans.findIndex(x => x.id === data.plan);
    const msg = up
      ? `Upgrade to ${planName(id)} now? You pay the prorated difference for the rest of this cycle, once.`
      : (id === data.plan ? `Keep ${planName(id)} and cancel the scheduled change?` : `Move to ${planName(id)}? ${p.downgrade_timing === 'immediate' ? 'It applies now.' : 'It applies at your next renewal; you keep your current features until then.'}`);
    if (window.confirm(msg)) await run(() => ts.changePlan(id), (r) => r.note);
  };
  const unassigned = (numbers.data || []).filter(n => !n.assigned_member_id && !n.seat_released);

  return (
    <Page title="Billing" subtitle={`${p.plan_name} plan · ${money(p.seat_price_cents)} per seat per month · ${p.included_minutes_per_seat} min and ${p.included_sms_per_seat} text segments included per seat, pooled`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ts-billing-stats">
        <Stat label="Seats" value={data.seats_purchased} hint={`${data.numbers_assigned} of ${data.numbers_total} numbers assigned`} />
        <Stat label={data.pending_plan ? 'Subscription from renewal' : 'Monthly subscription'} value={money(data.monthly_total_cents)}
          hint={data.pending_plan ? `${planName(data.pending_plan)} from ${when(data.billing.next_bill_at)}` : (data.billing.next_bill_at ? `Renews ${when(data.billing.next_bill_at)}` : '')} />
        <Stat label="Minutes used" value={`${u.minutes_used} / ${u.minutes_included}`} hint={u.minutes_over ? `${u.minutes_over} over · ${money(u.voice_overage_cents)}` : 'Within plan'} />
        <Stat label="Text segments used" value={`${u.sms_used} / ${u.sms_included}`} hint={u.sms_over ? `${u.sms_over} over · ${money(u.sms_overage_cents)}` : `Within plan · ${u.sms_messages} messages`} />
        {(p.features.includes('ai_front_desk') || u.ai_minutes_used > 0) && (
          <Stat label="AI Front Desk minutes" value={u.ai_minutes_used} hint={u.ai_minutes_billable ? `${u.ai_minutes_billable} billed · ${money(u.ai_cents)}` : (u.ai_minutes_from_voice ? `${u.ai_minutes_from_voice} from voice allowance` : 'Metered separately')} />
        )}
        <Stat label="Extra usage this cycle" value={money(u.overage_cents)} hint={data.spending_limit_cents ? `Limit ${money(data.spending_limit_cents)}` : 'No limit set'} />
        {data.credit_cents > 0 && <Stat label="Account credit" value={money(data.credit_cents)} hint="Used before your card is charged" />}
      </div>
      {cycleAlerts.length > 0 && (
        <ul className="space-y-1" data-testid="ts-usage-alerts">
          {cycleAlerts.map(a => <li key={a.id} className="text-sm text-amber-700 dark:text-amber-400 bg-amber-500/10 rounded px-3 py-1.5">{a.message}</li>)}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 items-center text-sm">
        Status: <Pill tone={data.status === 'active' ? 'green' : 'red'}>{data.status}</Pill>
        {data.billing.simulated && <Pill tone="amber">Test mode — no real charges</Pill>}
        {data.billing.status === 'past_due' && <Pill tone="red">Payment failed — update your card</Pill>}
      </div>

      {data.status === 'active' && (
        <>
          <PlanPicker pricing={p} current={data.plan} onPick={changePlan} label="Plan" />
          {data.pending_plan && (
            <p className="text-sm text-muted-foreground" data-testid="ts-pending-plan">
              Changing to {planName(data.pending_plan)} at your next renewal{data.billing.next_bill_at ? ` (${when(data.billing.next_bill_at)})` : ''}.{' '}
              <button className="text-primary underline" onClick={() => changePlan(data.plan)}>Keep {planName(data.plan)}</button>
            </p>
          )}
        </>
      )}

      <Section title="Add seats">
        {!adding ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="How many more"><Input type="number" min={1} value={extra} onChange={e => setExtra(e.target.value)} className="w-24" /></Field>
            <Button onClick={() => run(async () => { setCart(await ts.addSeats(Number(extra))); })} data-testid="ts-add-seats">Choose numbers</Button>
            <span className="text-xs text-muted-foreground">You pay a prorated amount for the rest of this billing cycle, then {money(p.seat_price_cents)} per added seat.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <NumberSearch cart={cart} onCart={setCart} />
            <CartPanel cart={cart} onCart={setCart} />
            <Button disabled={!cart?.complete} onClick={() => run(() => ts.checkout(), (r) => `Added ${r.seats_added || ''} seat(s) for ${money(r.charged_cents)}`)} data-testid="ts-add-seats-pay">Pay and add seats</Button>
          </div>
        )}
      </Section>

      <Section title="Remove seats">
        <p className="text-xs text-muted-foreground">Choose unassigned numbers to give up. They are queued for review and released by Agent9 support; nothing is released automatically. The lower price starts at your next renewal.</p>
        {unassigned.length === 0 ? <Empty>No unassigned numbers. Unassign one in Staff & Numbers first.</Empty> : (
          <div className="flex flex-wrap gap-3">
            {unassigned.map(n => (
              <label key={n.id} className="text-sm flex items-center gap-1">
                <input type="checkbox" checked={dropping.includes(n.id)} onChange={() => setDropping(d => d.includes(n.id) ? d.filter(x => x !== n.id) : [...d, n.id])} /> {n.display}
              </label>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" disabled={!dropping.length}
          onClick={() => window.confirm(`Give up ${dropping.length} seat(s)?`) && run(() => ts.reduceSeats(dropping), (r) => r.note).then(() => setDropping([]))}>
          Remove {dropping.length || ''} seat{dropping.length === 1 ? '' : 's'}
        </Button>
      </Section>

      <Section title="Spending limit">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Extra usage cap per month ($, 0 = no cap)"><Input type="number" min={0} value={limit} onChange={e => setLimit(e.target.value)} className="w-32" /></Field>
          <Button size="sm" variant="outline" onClick={() => run(() => ts.updateSection('settings', { spending_limit_cents: Math.round(Number(limit) * 100) }), 'Saved')}>Save</Button>
        </div>
        <p className="text-xs text-muted-foreground">When the cap is reached, outbound calls and texts pause until the next billing cycle. Inbound calls still ring. You get an alert at 80% and 100% of your minutes, texts and limit.</p>
        {p.features.includes('ai_front_desk') && (
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={data.ai_minutes_use_voice_allowance} data-testid="ts-ai-uses-voice"
              onChange={e => run(() => ts.updateSection('settings', { ai_minutes_use_voice_allowance: e.target.checked }), 'Saved')} />
            Let AI Front Desk minutes use unused calling minutes before they're billed at {p.ai_cents_per_minute}¢
          </label>
        )}
      </Section>

      <Section title="Invoices">
        {data.invoices.length === 0 ? <Empty>No invoices yet.</Empty> : (
          <Table head={['Date', 'What', 'Extra usage', 'Credit used', 'Charged to card', 'Status']}>
            {data.invoices.map(i => (
              <tr key={i.id}>
                <Td>{when(i.created_at)}</Td>
                <Td>{INVOICE_LABELS[i.type]?.(i) || i.type}</Td>
                <Td>{i.overage_cents != null ? money(i.overage_cents) : '—'}</Td>
                <Td>{i.credit_applied_cents ? money(i.credit_applied_cents) : '—'}</Td>
                <Td className="font-medium">{money(i.charged_cents ?? i.amount_cents)}</Td>
                <Td>{i.status}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {data.status !== 'canceled' && (
        <Section title="Cancel">
          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => {
            const reason = window.prompt('Cancel the Tele Sale subscription? Tell us why (optional).');
            if (reason !== null) run(() => ts.cancel(reason), (r) => r.note);
          }}>Cancel subscription</Button>
        </Section>
      )}
    </Page>
  );
}
